import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { logActivity } from '@/hooks/useActivityLog';

interface MentionableUser {
  id: string;
  name: string;
  type: 'user' | 'client';
  role?: string;
}

export const useMentions = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch mentionable users (Residential Aides, Case Managers, Supervisors)
  const { data: mentionableStaff = [] } = useQuery({
    queryKey: ['mentionable-staff'],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_mentionable_entities', { search_text: '' });
      if (error) throw error;

      return (data || [])
        .filter((item: any) => item.type === 'user')
        .map((item: any) => ({
          id: item.id,
          name: item.name || 'Unknown',
          type: 'user' as const,
          role: item.role || undefined
        }));
    }
  });

  // Fetch mentionable clients
  const { data: mentionableClients = [] } = useQuery({
    queryKey: ['mentionable-clients'],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_mentionable_entities', { search_text: '' });

      if (error) throw error;

      return (data || []).filter((item: any) => item.type === 'client').map((client: any) => ({
        id: client.id,
        name: client.name,
        type: 'client' as const
      }));
    }
  });

  // All mentionable entities
  const mentionables: MentionableUser[] = [...mentionableStaff, ...mentionableClients];

  // Create mentions
  const createMentions = useMutation({
    mutationFn: async ({
      text,
      alertId,
      noteId
    }: {
      text: string;
      alertId?: string;
      noteId?: string;
    }) => {
      if (!user) throw new Error('Not authenticated');

      // Parse @mentions from text
      const mentionRegex = /@\[([^\]]+)\]\(([^:]+):([^)]+)\)/g;
      const mentions: Array<{
        mentioned_user_id?: string;
        mentioned_client_id?: string;
        alert_id?: string;
        note_id?: string;
        created_by: string;
      }> = [];

      const mentionedUsers: Array<{ id: string; name: string; type: string }> = [];
      let hasClientMentions = false;

      let match;
      while ((match = mentionRegex.exec(text)) !== null) {
        const name = match[1];
        const type = match[2];
        const id = match[3];

        mentions.push({
          ...(type === 'user' ? { mentioned_user_id: id } : { mentioned_client_id: id }),
          ...(alertId ? { alert_id: alertId } : { note_id: noteId }),
          created_by: user.id
        });

        if (type === 'user') {
          mentionedUsers.push({ id, name, type });
        } else if (type === 'client') {
          hasClientMentions = true;
        }
      }

      if (mentions.length === 0) return [];

      const { data, error } = await supabase
        .from('mentions')
        .insert(mentions)
        .select();

      if (error) throw error;

      // Log activity for each mentioned user so it appears in their feed
      for (const mentionedUser of mentionedUsers) {
        await logActivity({
          actionType: 'create_note',
          actionDetails: {
            tagged_user_id: mentionedUser.id,
            tagged_user_name: mentionedUser.name,
            context: noteId ? 'note' : 'alert',
            entity_id: noteId || alertId,
          },
        });
      }
      // Get creator's name for notifications
      const { data: creatorProfile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .single();

      // Trigger email notifications for mentioned users
      for (const mentionedUser of mentionedUsers) {
        try {
          // Get note or alert details for the notification
          let entityTitle = '';
          let entityContent = text;

          if (noteId) {
            const { data: note } = await supabase
              .from('notes')
              .select('title, content')
              .eq('id', noteId)
              .single();
            entityTitle = note?.title || 'Untitled Note';
            entityContent = note?.content || text;
          } else if (alertId) {
            const { data: alert } = await supabase
              .from('alerts')
              .select('title, description')
              .eq('id', alertId)
              .single();
            entityTitle = alert?.title || 'Untitled Alert';
            entityContent = alert?.description || text;
          }

          await supabase.functions.invoke('send-mention-notification', {
            body: {
              mentionedUserId: mentionedUser.id,
              creatorName: creatorProfile?.full_name || 'Someone',
              entityType: noteId ? 'note' : 'alert',
              entityTitle,
              entityContent
            }
          });
        } catch (err) {
          console.error('Failed to send mention notification:', err);
        }
      }

      // Show toast if client mentions triggered Case Manager notifications
      if (hasClientMentions) {
        toast.success('Case Manager notified about client mention');
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mentions'] });
      queryClient.invalidateQueries({ queryKey: ['my-mentions'] });
      queryClient.invalidateQueries({ queryKey: ['user-mentions'] });
    },
    onError: (error) => {
      console.error('Error creating mentions:', error);
      toast.error('Failed to save mentions');
    }
  });

  // Get mentions for a user
  const { data: userMentions = [] } = useQuery({
    queryKey: ['user-mentions', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('mentions')
        .select(`
          *,
          alerts(id, title, description, type, created_at, site_id),
          notes(id, title, content, created_at, site_id)
        `)
        .eq('mentioned_user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!user
  });

  return {
    mentionables,
    mentionableStaff,
    mentionableClients,
    createMentions: createMentions.mutate,
    createMentionsAsync: createMentions.mutateAsync,
    userMentions
  };
};
